import { BlocksByJobIdMap } from "portal/views/reports/useHeatmapBlocks";
import { blockToPolygon, isBlockRenderable } from "portal/utils/blockGeometry";
import { entryTimingGradeToJSON } from "protos/portal/metrics";
import { Feature, FeatureCollection, Polygon } from "geojson";
import { featureCollection, Units } from "@turf/helpers";
import {
  getAvaliableSpatialMetricValue,
  SPATIAL_METRICS,
} from "portal/utils/spatialMetrics";
import {
  isSpatialMetricBoolean,
  isSpatialMetricDateTime,
  isSpatialMetricDiscrete,
  isSpatialMetricNumber,
} from "portal/utils/metrics";
import {
  NormalizedBlock,
  normalizeJigsawBlock,
  normalizeOldBlock,
} from "portal/utils/blocks";

export interface JobInsightsExportArgs {
  /** Per-job block map exposed by `useHeatmapBlocks` (matches what's plotted). */
  blocksByJobId: BlocksByJobIdMap;
  /** jobId -> human-friendly job name. */
  jobNames: Map<string, string>;
  /** jobId -> robot serial (jigsaw blocks carry no serial, so this is required). */
  jobSerials: Map<string, string>;
  /** Units for the width-fallback geometry; use the same value the map uses. */
  units: Units;
}

type ExportProperties = Record<string, number | string>;

/**
 * Build a GeoJSON FeatureCollection of block swath Polygons (WGS84) for the
 * currently-selected Job Insights jobs, suitable for import into ArcGIS.
 *
 * Each feature is one block's swath (via the same `blockToPolygon` the map
 * uses) with raw numeric attributes: every applicable metric from
 * `SPATIAL_METRICS` plus identifiers (jobId, jobName, robotSerial, blockId,
 * timestamp, start/end coordinates, swath width/length).
 *
 * Blocks are pre-filtered to match what the heatmap renders (suspicious,
 * missing, or probably-wrong coordinates are dropped).
 */
export const buildJobInsightsGeoJson = ({
  blocksByJobId,
  jobNames,
  jobSerials,
  units,
}: JobInsightsExportArgs): FeatureCollection<Polygon> => {
  const features: Feature<Polygon>[] = [];

  const addBlock = (block: NormalizedBlock, jobId: string): void => {
    // Shares isBlockRenderable with useMapBlocks so the export matches the plot.
    if (!isBlockRenderable(block)) {
      return;
    }

    const feature = blockToPolygon(block, { units });
    if (!feature) {
      return;
    }

    // blockToPolygon injects _width/_length (feet) into properties; reuse those
    // computed values under clear names and drop the underscored keys.
    const widthFt = feature.properties?._width;
    const lengthFt = feature.properties?._length;

    const properties: ExportProperties = {
      jobId,
      jobName: jobNames.get(jobId) ?? jobId,
      robotSerial: jobSerials.get(jobId) ?? "",
    };

    const blockId = block.originalBlock.block.db?.id;
    if (blockId !== undefined) {
      properties.blockId = Number(blockId);
    }
    properties.timestampMs = Number(block.start.timestampMs);
    properties.startLat = block.start.latitude;
    properties.startLon = block.start.longitude;
    properties.endLat = block.end.latitude;
    properties.endLon = block.end.longitude;
    if (typeof widthFt === "number") {
      properties.widthFt = widthFt;
    }
    if (typeof lengthFt === "number") {
      properties.lengthFt = lengthFt;
    }

    // Raw metric values (native units — no measurement-system formatting).
    for (const metric of SPATIAL_METRICS) {
      if (isSpatialMetricNumber(metric)) {
        // Covers duration metrics too (both are type "number").
        const value = getAvaliableSpatialMetricValue(metric, block);
        if (value !== undefined && Number.isFinite(value)) {
          properties[metric.id] = value;
        }
      } else if (isSpatialMetricDiscrete(metric)) {
        const value = getAvaliableSpatialMetricValue(metric, block);
        if (value !== undefined) {
          properties[metric.id] = entryTimingGradeToJSON(value);
        }
      } else if (isSpatialMetricBoolean(metric)) {
        const value = getAvaliableSpatialMetricValue(metric, block);
        if (value !== undefined) {
          properties[metric.id] = value ? 1 : 0;
        }
      } else if (isSpatialMetricDateTime(metric)) {
        const value = getAvaliableSpatialMetricValue(metric, block);
        if (value !== undefined) {
          properties[metric.id] = value.getTime();
        }
      }
      // string metrics are skipped (raw-numeric export)
    }

    feature.properties = properties;
    features.push(feature);
  };

  if (blocksByJobId.type === "jigsaw") {
    for (const [jobId, entry] of Object.entries(blocksByJobId.map)) {
      for (const raw of entry?.blocks ?? []) {
        addBlock(normalizeJigsawBlock(raw), jobId);
      }
    }
  } else {
    for (const [jobId, entry] of Object.entries(blocksByJobId.map)) {
      for (const raw of entry?.blocks ?? []) {
        addBlock(normalizeOldBlock(raw), jobId);
      }
    }
  }

  return featureCollection(features);
};
